import { url, cleanEnv, defaultReporter, str } from 'envalid';

/*
 * Variables which VARY across environments.
 *
 * DO NOT add secret credentials here.
 * DO NOT add static/constant configuration here.
 *
 * ========================================================================
 * WARNING: Some vars are exposed to `__ENVIRONMENT__`. Check root.tsx.
 * =======================================================================
 */

declare global {
  interface Window {
    __ENVIRONMENT__?: Record<string, string>;
  }
}
// biome-ignore lint/nursery/noProcessEnv: This is the only place that's okay
const isNode = typeof window === 'undefined' || process.env.NODE_ENV === 'test';
// biome-ignore lint/nursery/noProcessEnv: This is the only place that's okay
const vars = isNode ? process.env : window.__ENVIRONMENT__;

/*
  `devDefault`: local development env.
  `default`: all deployed envs (i.e. testing, staging, production).
*/
const env = cleanEnv(
  vars,
  {
    NODE_ENV: str({
      devDefault: 'development',
      choices: ['development', 'test', 'production', 'staging'],
    }),
    DEPLOYMENT_ENVIRONMENT: str({
      devDefault: 'development',
      choices: ['development', 'testing', 'staging', 'production'],
    }),
    KAMELEOON_SITE_CODE: str({
      devDefault: 'l3n0djpu9w',
      default: 'l3n0djpu9w',
    }),
    KAMELEOON_CLIENT_SECRET: str({
      devDefault: '1zN7rjZobr2pXkMheoLCk-Wo5l-f-YBbVMA4kIWJCw4',
      default: '1zN7rjZobr2pXkMheoLCk-Wo5l-f-YBbVMA4kIWJCw4',
    }),
    KAMELEOON_CLIENT_ID: str({
      devDefault: '30231-maciek-wrona-native-instruments-com',
      default: '30231-maciek-wrona-native-instruments-com',
      choices: [
        '30231-maciek-wrona-native-instruments-com',
        '29772-diessica-gurskas-native-instruments-com',
      ],
    }),
    ALGOLIA_APP_ID: str({
      devDefault: 'OHVKP52PE9',
      choices: ['OHVKP52PE9', '19X89MP6MT'],
    }),
    ALGOLIA_API_KEY: str({
      devDefault: '8d89bca60170ee83a783f0599cb6c271',
    }),
    ALGOLIA_INDEX_SUFFIX: str({
      devDefault: 'dev',
      choices: ['dev', 'prod'],
      desc: 'An environment suffix added to the index name e.g. `webshop_products_en_prod`. When changing ALGOLIA_APP_ID, also change this.',
    }),

    /* === Client ========================================================== */
    APP_BASE_URL: url({
      devDefault: 'https://www.dev.ni.de',
      choices: [
        'https://www.dev.ni.de',
        'https://www.testing.native-instruments.com',
        'https://www.staging.native-instruments.com',
        'https://www.native-instruments.com',
      ],
    }),
    APP_COOKIE_DOMAIN: str({
      devDefault: '.ni.de',
      choices: [
        '.ni.de',
        '.testing.native-instruments.com',
        '.staging.native-instruments.com',
        '.native-instruments.com',
      ],
    }),
    APP_TAG_MANAGER_PROFILE: str({
      devDefault: 'GTM-92L2',
      choices: [
        'GTM-92L2&gtm_preview=env-229', // dev
        'GTM-92L2', // production
      ],
    }),
    APP_ADYEN_CLIENT_KEY: str({
      devDefault: 'test_DUPHYCO5J5GDBHJR427WA2GWUYX6BIDF',
      default: 'test_DUPHYCO5J5GDBHJR427WA2GWUYX6BIDF',
      choices: [
        'test_DUPHYCO5J5GDBHJR427WA2GWUYX6BIDF', // dev & staging
        'live_TITOXJUIBFATXF35LTP7FUO4RIAFXKL2', // production
      ],
    }),

    /* === Monitoring ======================================================= */
    APP_SENTRY_AUTH_TOKEN: str({
      devDefault:
        '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',
      default:
        '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',
    }),
    APP_SENTRY_DSN: str({
      devDefault:
        'https://06369e7918ddf79e7f46882f08482614@o144199.ingest.us.sentry.io/4508416120324097',
      default:
        'https://06369e7918ddf79e7f46882f08482614@o144199.ingest.us.sentry.io/4508416120324097',
    }),
    LOG_LEVEL: str({
      devDefault: 'info',
      default: 'info',
      choices: [
        'emerg',
        'alert',
        'crit',
        'error',
        'warning',
        'notice',
        'info',
        'debug',
      ],
      docs: 'https://github.com/winstonjs/winston#logging-levels',
    }),
  },
  {
    reporter: vars?.NODE_ENV === 'production' ? () => null : defaultReporter,
  },
);

export default env;
